/**
 * Server function bridge for SSR tRPC calls.
 *
 * The `createServerFn` boundary is required by TanStack Start's import protection
 * so that `.server.ts` imports (appRouter, createTRPCContext) are tree-shaken from
 * the client bundle. On SSR the call stays in-process — no HTTP round-trip.
 *
 * A passthrough `inputValidator` is used instead of Zod to type `data` without
 * serialization/deserialization overhead — the input comes from the typed tRPC
 * client (trusted in-process).
 */
import { createServerFn } from '@tanstack/react-start';
import { callTRPCProcedure, TRPCError } from '@trpc/server';
import { appRouter } from './trpc/router.server';
import { createTRPCContext } from './trpc/init.server';

type TRPCCallInput = { path: string; input: unknown; type: 'query' | 'mutation' };

export const trpcSSRCallFn = createServerFn({ method: 'POST' })
	.inputValidator((input: TRPCCallInput) => input)
	.handler(async ({ data }) => {
		try {
			return await callTRPCProcedure({
				router: appRouter,
				path: data.path,
				getRawInput: () => Promise.resolve(data.input),
				ctx: createTRPCContext(),
				type: data.type,
				signal: undefined,
				batchIndex: 0,
			});
		} catch (err) {
			// Convert UNAUTHORIZED to a data result so SSR doesn't crash.
			// Data hooks check `'unauthenticated' in data` and redirect to login.
			if (err instanceof TRPCError && err.code === 'UNAUTHORIZED') {
				return { unauthenticated: true };
			}
			throw err;
		}
	});
